import * as React from "react"
import "react-day-picker/lib/style.css"
import injectSheet from "react-jss"
import cn from "classnames"
import format from "date-fns/format"
import parse from "date-fns/parse"
import toDate from "date-fns/toDate"
import { navbarStyles, styles, weekdayStyles } from "./styles"
import DayPicker from "react-day-picker"
import { Icon } from "@xingternal/360-components"
import { useIsRebrandingEnabled } from "@xingternal/rebranding-adapter"

import en from "date-fns/locale/en-GB"
import de from "date-fns/locale/de"

const getLocale = (locale) => (locale === "en" ? en : de)

const formatDay = (day, locale = "en") => {
  return format(day, "LLLL do", { locale: getLocale(locale) })
}

const formatMonthTitle = (date, locale = "en") => {
  return format(date, "MMMM yyyy", { locale: getLocale(locale) })
}

const formatWeekdayShort = (day, locale = "en") => {
  return getLocale(locale).localize.day(day, { width: "short" })
}

const formatWeekdayLong = (day, locale = "en") => {
  return getLocale(locale).localize.day(day)
}

const getFirstDayOfWeek = (locale = "en") => {
  return getLocale(locale).options.weekStartsOn
}

const getMonths = (locale = "en") => {
  const loc = getLocale(locale)
  const months = []
  let i = 0
  while (i < 12) {
    months.push(loc.localize.month(i))
    i += 1
  }
  return months
}

const formatDate = (date, fmt = "L", locale = "en") => {
  const outputFormat = Array.isArray(fmt) ? fmt[0] : fmt

  return format(date, outputFormat, { locale: getLocale(locale) })
}

const parseDate = (str, fmt = "L", locale = "en") => {
  const d = parse(str, fmt, new Date(), { locale: getLocale(locale) })

  if (d.isValid()) return toDate(d)
  return undefined
}

const localeUtils = {
  formatDay,
  formatMonthTitle,
  formatWeekdayShort,
  formatWeekdayLong,
  getFirstDayOfWeek,
  getMonths,
  formatDate,
  parseDate,
}

const Navbar = injectSheet(navbarStyles)(
  ({
    onPreviousClick,
    onNextClick,
    classes,
    showNextButton,
    showPreviousButton,
  }) => (
    <div className={classes.monthsNav}>
      {showPreviousButton && (
        <div className={classes.monthsPrev} onClick={() => onPreviousClick()}>
          <Icon name="shape-arrow-left" size={16} />
        </div>
      )}
      {showNextButton && (
        <div className={classes.monthsNext} onClick={() => onNextClick()}>
          <Icon name="shape-arrow-right" size={16} />
        </div>
      )}
    </div>
  ),
)

const Weekday = injectSheet(weekdayStyles)(({
  classes,
  locale,
  localeUtils,
  weekday,
}) => {
  const isWorkday = weekday !== 0 && weekday !== 6
  const weekdayNameShort = localeUtils.formatWeekdayShort(weekday, locale)
  const weekdayName = localeUtils.formatWeekdayLong(weekday, locale)
  return (
    <div
      className={cn(classes.weekday, { [classes.workday]: isWorkday })}
      title={weekdayName}
    >
      {weekdayNameShort}
    </div>
  )
})

const DatePicker = ({
  className,
  disabledDays,
  locale,
  modifiers,
  numberOfMonths,
  onDayClick,
  selectedDays,
  toMonth,
}) => {
  const { enableXingRebranding } = useIsRebrandingEnabled()

  const datePickerClassName = enableXingRebranding
    ? "DatePicker DatePickerRebranded"
    : "DatePicker"

  return (
    <DayPicker
      className={cn(datePickerClassName, className)}
      disabledDays={disabledDays}
      firstDayOfWeek={localeUtils.getFirstDayOfWeek()}
      modifiers={modifiers}
      numberOfMonths={numberOfMonths}
      locale={locale}
      localeUtils={localeUtils}
      onDayClick={onDayClick}
      selectedDays={selectedDays}
      navbarElement={<Navbar />}
      weekdayElement={<Weekday />}
      toMonth={toMonth}
    />
  )
}

export const StyledDatePicker = injectSheet(styles)(DatePicker)
export default DatePicker
